import { ref } from "vue";
import { defineStore } from "pinia";
import { useAuthStore } from "@/stores/auth";
import { useRouter } from "vue-router";
import DBConfigService from "@/core/services/DBConfigService";
// import tables from `../core/worker/tables.json`
/**
 * Die entschlüsselte `user`-Zeile aus der Konfigurationsdatenbank, wie der
 * ACLQueryHandler sie nach der Rechteauflösung liefert
 * (`datasource: JSON.stringify(result.user)` in query.worker.ts).
 *
 * Alle Felder sind optional, weil das Objekt vor dem Login und bis zur ersten
 * ACL-Antwort schlicht `{}` ist — genau darauf verlassen sich die Leser mit
 * ihren `?.`-Ketten. Der Index-Signatur-Rest hält die Zeile offen: sie kommt
 * roh aus der Datenbank und trägt mehr Spalten, als hier interessieren.
 *
 * Vorher stand hier `{}`. Ein leeres Objektliteral hat KEINE Eigenschaften,
 * also war jeder Zugriff auf `.at`/`.id` ein Typfehler — im Code an mehreren
 * Stellen mit `(userStore.Userdata as any)?.datasource` umgangen. Diese Casts
 * sind mit dem Typ hier überflüssig geworden.
 */
export interface DataSource {
  /** Access-Token (JWT). Im Klartext, sobald renewTokenForWorker gelaufen ist. */
  at?: string
  /** Refresh-Token. */
  rt?: string
  /** User-Id — erst nach erfolgreicher ACL-Ableitung gesetzt. */
  id?: string
  /** Geräte-Id. */
  di?: string
  /** E-Mail-Adresse des angemeldeten Kontos. */
  em?: string
  [key: string]: any
}

export const useUserStore = defineStore('user', {
  // arrow function recommended for full type inference
  state: () => {
    return {
      Userdata: {
        id: '',
        deviceid: '',
        companies: [],
        credentials: [],
        datasource: {} as DataSource,
        useracls: {},
        userrightsbycompany: {},
        ActiveCompanyResource: {},
        CompaniesResources: [],
        UserCompaniesIds: [],
        timestamp: '',
      },
      UserSettings: {
        reports: {
          year: new Date().getFullYear()
        },
        calendar: {
          CalenderViewMode: 'studio', // auf activesettingsperview umbauen
          ShowResources: false, // auf activesettingsperview umbauen
          ShowArchivedTrainings: false,
        },
        activesettingspervieworiginalstructure: {
          Calendar: {
            timezone: '',
            calendarViewMode: 'week',
            startOfWeek: 1,
            showTimeStepsInMinutes: 60,
            ViewedTimeStepsInMinutes: 15,
            ShowResources: true,
            showarchivedtrainings: false,
            ActiveDate: "2022-03-02", // new Date().formatDate("YYYY-MM-DD"),
            ActiveWeek: new Date("2022-03-02").getWeekNumber(),
            ActiveMonth: new Date("2022-03-02").getMonth(),
            ActiveYear: new Date("2022-03-02").getFullYear()
          },
          Studio: {
            activeroomid: '',
            activelocationid: '',
            activeroute: '',
          },
          Client: {
            listsettings: {
              groups: [],
              status: [],
              locations: [],
              scrollpositiontop: 0,
              searchquery: ''
            }
          }
        },
        activesettingsperview: {} as Record<string, any>
      },
      dateconfig: {
        ActiveDate: "2022-03-02", // new Date().formatDate("YYYY-MM-DD"),
        ActiveWeek: new Date("2022-03-02").getWeekNumber(),
        ActiveMonth: new Date("2022-03-02").getMonth(),
        ActiveYear: new Date("2022-03-02").getFullYear(),
        DateTimer: [],
        StartOfWeek: 1,
        // Beschriftungsraster der Zeitspalte (Tag/Woche/Monat): Halbstundentakt
        ShowTimeStepsInMinutes: 30,
        ShowResources: true,
        showarchivedtrainings: false,
        ViewedTimeStepsInMinutes: 15,
        cellheight: 50,
        cellwidth: '',
        sizechanged: '',
        day: {
          cellheight: '',
          cellwidth: ''
        },
        CalendarView: 'week',
        cachingevent: {
          month: '',
          start: '',
          end: ''
        }
      },
    }
  },
  actions: {
    checkactivesettings() {
      console.log('activesettings')
      console.log(Array.isArray(this.UserSettings.activesettingsperview))
      if (this.UserSettings.activesettingsperview.constructor === Array || this.UserSettings.activesettingsperview.length === 0) {
        console.log('Alles gut')
        // Companies und Dummydatenstruktur einbauen.
      }
    },
    sendviewsettings(state, data) {
      console.log('sendviewsettings')
      console.log(data)
    },
    setcelldimensionsweek(state, data) {
      this.dateconfig.cellheight = data.height
      this.dateconfig.cellwidth = data.width
    },
    setDateConfig(parameter, view) {
      this.dateconfig[parameter] = view
    },
    setUserData(parameter, view) {
      this.Userdata[parameter] = view
      if (view === 'ActiveUserCompanyACLRights') this.checkactivesettings()
    },
    setActiveSettings(setting, company, parameter, value) {
      if (setting !== "ActiveSettings") {
        this.UserSettings[setting] = value
      } else {
        this.UserSettings.activesettingsperview[company][parameter] = value
      }
    },
    initiateViewSettings() {
      console.log(this.Userdata.companies)
      console.log(Object.keys(this.UserSettings.activesettingsperview).length)
      if (this.Userdata.companies.length > 0 && Object.keys(this.UserSettings.activesettingsperview).length === 0) {
        console.log('Nachtragen')
      }
    },
    getactivesettingsperviewfromdb() {
      DBConfigService.getuserfromstorage({ id: this.Userdata.id })
        .then(function (userdata) {
          console.log(typeof userdata.vs)
        })
    },
    changeactivedate(data) {
      let date = new Date(this.dateconfig.ActiveDate)
      console.log('ActiveDate: ' + date.formatDate('YYYY-MM-DD'))
      console.log(data)
      if (data.condition === 'm') {
        date.setMonth(date.getMonth() + data.parameter)
        this.dateconfig.ActiveDate = date.formatDate('YYYY-MM-DD')
        this.dateconfig.ActiveMonth = date.getMonth()
        this.dateconfig.ActiveWeek = date.getWeekNumber()
        // store.dispatch('refreshcalendarcaches2', date.formatDate('YYYY-MM-DD'))
      } else if (data.condition === 'w') {
        date.setDate(date.getDate() + (data.parameter * 7))
        this.dateconfig.ActiveDate = date.formatDate('YYYY-MM-DD')
        if (this.dateconfig.ActiveMonth !== date.getMonth()) {
          this.dateconfig.ActiveMonth = date.getMonth()
          // store.dispatch('refreshcalendarcaches2', date.formatDate('YYYY-MM-DD'))
        }
        this.dateconfig.ActiveWeek = date.getWeekNumber()
      } else if (data.condition === 'd') {
        date.setDate(date.getDate() + (data.parameter))
        this.dateconfig.ActiveDate = date.formatDate('YYYY-MM-DD')
        if (this.dateconfig.ActiveMonth !== date.getMonth()) {
          this.dateconfig.ActiveMonth = date.getMonth()
          // store.dispatch('refreshcalendarcaches2', date.formatDate('YYYY-MM-DD'))
        }
        this.dateconfig.ActiveWeek = date.getWeekNumber()
      }
      if (this.dateconfig.ActiveYear !== date.getFullYear()) {
        this.dateconfig.ActiveYear = date.getFullYear()
      }
      // if (store.state.dateconfig.ActiveMonth !== date.getMonth()) store.dispatch('refreshcalendarcaches2', date.formatDate('YYYY-MM-DD'))
      console.log(this.dateconfig.ActiveDate)
      return false
    },
  }
})
